import { useState, useEffect, useRef } from "react";
import { axiosInstance } from "../App";
import { useBox } from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Download, RefreshCw, Search, Settings, Loader2, Wifi, WifiOff, Trash2, ArrowDownLeft, ArrowUpRight, MessageCircle, Check, Calendar, Eye, ArrowLeftRight } from "lucide-react";
import { getErrorMessage } from "../utils/errorHandler";
import { openWhatsApp } from "../utils/whatsappLink";
import { todayCR } from "../utils/dateHelpers";
import BankTransfersTab from "../components/BankTransfersTab";

export default function SinpeVerify({ user }) {
  const { selectedBox } = useBox();
  const [configured, setConfigured] = useState(false);
  const [email, setEmail] = useState("");
  const [fetching, setFetching] = useState(false);
  const [sinpeList, setSinpeList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rawBodyOpen, setRawBodyOpen] = useState(false);
  const [rawBodyContent, setRawBodyContent] = useState(null);
  // Auto-refresh is OPT-IN via localStorage. Default OFF because it:
  // (a) competes with the manual "Consultar" button (Gmail allows only
  //     one IMAP session per account);
  // (b) consumes deploy credits while the user is not actively watching.
  // User can enable it with the switch — state persists per device.
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {
    try { return localStorage.getItem("sinpe_autorefresh_enabled") === "1"; } catch (e) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("sinpe_autorefresh_enabled", autoRefreshEnabled ? "1" : "0"); } catch { /* ignored */ }
  }, [autoRefreshEnabled]);

  const openRawBody = (sinpe) => {
    setRawBodyContent({
      from: sinpe.from_email || "",
      subject: sinpe.raw_subject || "",
      bank: sinpe.bank || "",
      sender_name: sinpe.sender_name || "",
      direction: sinpe.direction || "",
      amount: sinpe.amount || 0,
      body: sinpe.raw_body || "(sin cuerpo disponible)",
    });
    setRawBodyOpen(true);
  };

  const copyRawBody = async () => {
    if (!rawBodyContent) return;
    try {
      await navigator.clipboard.writeText(rawBodyContent.body);
      toast.success("Cuerpo copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("entrante");
  const [sentIds, setSentIds] = useState(new Set());

  // Date filter
  const [filterMode, setFilterMode] = useState("today"); // today, days, range
  const [days, setDays] = useState("7");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Configure dialog
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configEmail, setConfigEmail] = useState("");
  const [configPassword, setConfigPassword] = useState("");
  const [configWhatsapp, setConfigWhatsapp] = useState("");
  const [configuring, setConfiguring] = useState(false);

  // Top-level view tabs: 'sinpe' (default) | 'transfers'
  const [topView, setTopView] = useState("sinpe");

  const isMaster = user?.role === "master";
  const whatsappNumber = selectedBox?.sinpe_whatsapp || "";

  // In-flight guard ref – prevents concurrent /sinpe/fetch calls (manual +
  // silent auto-refresh) that would trigger Gmail's "Too many simultaneous
  // connections" error. Using a ref (not state) so updates are synchronous
  // and don't trigger re-renders.
  const fetchInFlightRef = useRef(false);

  // Set today's date as default
  useEffect(() => {
    const today = todayCR();
    setDateFrom(today);
    setDateTo(today);
  }, []);

  useEffect(() => {
    if (selectedBox) {
      checkStatus();
      loadSentIds();
      setSinpeList([]);
    }
  }, [selectedBox]);

  // Auto-refresh: cuando la caja está abierta y SINPE está configurado,
  // consultamos el buzón cada 15 segundos en modo silencioso.
  // Sólo notificamos por toast si llegó un SINPE nuevo (no spam).
  const [cashOpen, setCashOpen] = useState(false);
  useEffect(() => {
    if (!selectedBox) { setCashOpen(false); return; }
    let cancelled = false;
    const checkCash = async () => {
      try {
        const resp = await axiosInstance.get(`/cash-register/current?box_id=${selectedBox.id}`);
        if (!cancelled) setCashOpen(!!resp.data);
      } catch (e) {
        if (!cancelled) setCashOpen(false);
      }
    };
    checkCash();
    const i = setInterval(checkCash, 30000); // re-evaluar estado de caja cada 30s
    return () => { cancelled = true; clearInterval(i); };
  }, [selectedBox]);

  useEffect(() => {
    if (!configured || !cashOpen || !selectedBox || !autoRefreshEnabled) return;
    // Silent auto-refresh every 60 seconds. Disabled by default; the user
    // explicitly enables it via the switch when they want it.
    const id = setInterval(() => {
      handleFetchSinpe(true);
    }, 60000);
    return () => clearInterval(id);
  }, [configured, cashOpen, selectedBox, filterMode, days, dateFrom, dateTo, autoRefreshEnabled]);

  const loadSentIds = async () => {
    if (!selectedBox) return;
    try {
      const resp = await axiosInstance.get(`/sinpe/sent-ids?box_id=${selectedBox.id}`);
      setSentIds(new Set(resp.data.sent_ids || []));
    } catch { /* ignored */ }
  };

  const checkStatus = async () => {
    if (!selectedBox) return;
    try {
      const resp = await axiosInstance.get(`/sinpe/status?box_id=${selectedBox.id}`);
      setConfigured(resp.data.configured);
      setEmail(resp.data.email || "");
    } catch (e) {
      setConfigured(false);
    }
  };

  const handleConfigure = async () => {
    const cleanWa = (configWhatsapp || "").replace(/\D/g, "");
    const credsProvided = !!(configEmail && configPassword);
    // If the user ONLY wants to update/clear the WhatsApp number (left email
    // and password untouched), use the lightweight whatsapp-only endpoint
    // which does not re-verify IMAP credentials and supports clearing.
    if (!credsProvided) {
      if (!selectedBox?.id) { toast.error("Sin caja seleccionada"); return; }
      setConfiguring(true);
      try {
        await axiosInstance.post("/sinpe/whatsapp", {
          box_id: selectedBox.id,
          whatsapp_number: cleanWa,
        });
        toast.success(cleanWa ? "Número WhatsApp actualizado" : "Número WhatsApp removido");
        // Reflect the new value in-memory without a page reload (so the
        // selected box is preserved).
        if (selectedBox) {
          selectedBox.sinpe_whatsapp = cleanWa;
        }
        setShowConfigDialog(false);
        checkStatus();
      } catch (error) {
        toast.error(getErrorMessage(error, "Error al actualizar WhatsApp"));
      } finally { setConfiguring(false); }
      return;
    }

    // Full (re)configuration path - requires email + app password
    setConfiguring(true);
    try {
      await axiosInstance.post("/sinpe/configure", {
        box_id: selectedBox.id,
        email: configEmail,
        app_password: configPassword,
        whatsapp_number: cleanWa,
      });
      toast.success("Configuración SINPE guardada");
      if (selectedBox) {
        selectedBox.sinpe_email = configEmail;
        selectedBox.sinpe_whatsapp = cleanWa;
      }
      setShowConfigDialog(false);
      setConfigEmail(""); setConfigPassword(""); setConfigWhatsapp("");
      checkStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al configurar"));
    } finally { setConfiguring(false); }
  };

  const handleRemove = async () => {
    if (!window.confirm("¿Quitar correo SINPE de esta caja?")) return;
    try {
      await axiosInstance.post("/sinpe/remove", { box_id: selectedBox.id });
      toast.success("Correo SINPE removido");
      setConfigured(false); setEmail(""); setSinpeList([]);
    } catch (error) { toast.error(getErrorMessage(error, "Error al remover")); }
  };

  const handleFetchSinpe = async (silent = false) => {
    // In-flight guard: if a fetch is already running, drop silent retries
    // and signal a "busy" toast on manual click. This prevents Gmail's
    // "[ALERT] Too many simultaneous connections" error when the user
    // clicks Consultar while the silent auto-refresh is mid-flight.
    if (fetchInFlightRef.current) {
      if (!silent) toast.info("Consulta en progreso, espere un momento…");
      return;
    }
    fetchInFlightRef.current = true;
    if (!silent) setFetching(true);
    try {
      let url = `/sinpe/fetch?box_id=${selectedBox.id}`;
      if (filterMode === "today") {
        url += "&days=0";
      } else if (filterMode === "days") {
        url += `&days=${days}`;
      } else if (filterMode === "range") {
        if (!dateFrom) {
          if (!silent) { toast.error("Seleccione fecha inicio"); setFetching(false); }
          fetchInFlightRef.current = false;
          return;
        }
        url += `&date_from=${dateFrom}`;
        if (dateTo) url += `&date_to=${dateTo}`;
      }
      // Incremental fetch: when we already have SINPEs loaded for this box,
      // pass the timestamp of the most recent one as a floor. Backend will
      // only return emails strictly newer than that, dramatically speeding
      // up subsequent "Consultar" clicks. We keep the existing list and
      // merge the new entries on top.
      let sinceIso = null;
      if (sinpeList.length > 0) {
        const latest = sinpeList.reduce((acc, s) => {
          const t = s.parsed_date || s.date || "";
          return (!acc || t > acc) ? t : acc;
        }, "");
        if (latest) {
          sinceIso = latest;
          url += `&since_iso=${encodeURIComponent(latest)}`;
        }
      }

      const resp = await axiosInstance.get(url, { timeout: 90000 });
      const incoming = resp.data.sinpe_list || [];

      if (sinceIso) {
        // Incremental mode: merge with current list, dedupe by email_id.
        const seen = new Set(sinpeList.map((s) => s.email_id || `${s.parsed_date}|${s.amount}|${s.sender_name}`));
        const fresh = incoming.filter((s) => !seen.has(s.email_id || `${s.parsed_date}|${s.amount}|${s.sender_name}`));
        const merged = [...fresh, ...sinpeList].sort((a, b) => (b.parsed_date || "").localeCompare(a.parsed_date || ""));
        setSinpeList(merged);
        if (silent) {
          if (fresh.length > 0) toast.success(`🔔 ${fresh.length} SINPE nuevo(s)`);
        } else {
          toast.success(fresh.length > 0 ? `${fresh.length} SINPE nuevo(s)` : "Sin SINPE nuevos");
        }
      } else {
        // First fetch — replace the list entirely.
        const prevCount = sinpeList.length;
        setSinpeList(incoming);
        if (silent) {
          if (incoming.length > prevCount) toast.success(`🔔 ${incoming.length - prevCount} SINPE nuevo(s)`);
        } else {
          toast.success(`${resp.data.total} SINPE encontrados`);
          if (resp.data.truncated && resp.data.truncated_message) {
            toast.warning(resp.data.truncated_message, { duration: 8000 });
          }
        }
      }
    } catch (error) {
      if (!silent) toast.error(getErrorMessage(error, "Error al consultar SINPE"));
    } finally {
      if (!silent) setFetching(false);
      fetchInFlightRef.current = false;
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const resp = await axiosInstance.get("/sinpe/export-excel");
      if (resp.data.excel_base64) {
        const byteCharacters = atob(resp.data.excel_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) { byteNumbers[i] = byteCharacters.charCodeAt(i); }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = resp.data.filename || "SINPE.xlsx"; a.click();
        window.URL.revokeObjectURL(url);
        toast.success("Excel descargado");
      }
    } catch (error) { toast.error(getErrorMessage(error, "Error al exportar")); }
    finally { setExporting(false); }
  };

  const handleSendWhatsapp = async (sinpe) => {
    const number = whatsappNumber || "";
    const dir = sinpe.direction === "saliente" ? "SALIENTE" : "ENTRANTE";
    const msg = `*SINPE ${dir}*\nNombre: ${sinpe.sender_name || "Desconocido"}\nMonto: ₡${sinpe.amount?.toLocaleString() || "0"}\nBanco: ${sinpe.bank || "-"}\n${sinpe.sender_phone ? `Tel: ${sinpe.sender_phone}\n` : ""}${sinpe.reference ? `Ref: ${sinpe.reference}\n` : ""}${sinpe.description ? `Motivo: ${sinpe.description}\n` : ""}Fecha: ${sinpe.date ? new Date(sinpe.date).toLocaleString("es-CR") : "-"}`;
    openWhatsApp(number, msg);
    const id = getSinpeId(sinpe);
    try { await axiosInstance.post("/sinpe/mark-sent", { sinpe_id: id, box_id: selectedBox.id }); } catch { /* ignored */ }
    setSentIds(prev => new Set([...prev, id]));
  };

  const getSinpeId = (sinpe) => sinpe.reference || sinpe.email_id || `${sinpe.amount}-${sinpe.date}`;
  const isSent = (sinpe) => sentIds.has(getSinpeId(sinpe));

  const entrantes = sinpeList.filter(s => s.direction === "entrante");
  const salientes = sinpeList.filter(s => s.direction === "saliente");
  const displayed = activeTab === "entrante" ? entrantes : activeTab === "saliente" ? salientes : sinpeList;
  const filtered = displayed.filter(s => !searchTerm || (s.sender_name || "").toLowerCase().includes(searchTerm.toLowerCase()) || (s.reference || "").toLowerCase().includes(searchTerm.toLowerCase()) || (s.sender_phone || "").includes(searchTerm));
  const totalEntrante = entrantes.reduce((s, x) => s + (x.amount || 0), 0);
  const totalSaliente = salientes.reduce((s, x) => s + (x.amount || 0), 0);
  const filteredTotal = filtered.reduce((s, x) => s + (x.amount || 0), 0);

  if (!selectedBox) return <div className="p-8 text-center text-slate-500">Seleccione una caja</div>;

  return (
    <div className="space-y-6 p-4 lg:p-6" data-testid="sinpe-verify-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">
            {topView === "sinpe" ? "Verificador SINPE" : "Verificador Transferencias Bancarias"}
          </h1>
          <p className="text-slate-500">Caja: {selectedBox.name}</p>
        </div>
        <div className="flex items-center gap-2">
          {topView === "sinpe" && configured && cashOpen && (
            <label
              data-testid="sinpe-autorefresh-switch"
              className={`inline-flex items-center gap-2 text-xs font-medium rounded-full px-3 py-1 border cursor-pointer transition-colors select-none ${
                autoRefreshEnabled
                  ? "text-green-700 bg-green-50 border-green-200"
                  : "text-slate-600 bg-slate-100 border-slate-300"
              }`}
              title={
                autoRefreshEnabled
                  ? "Consultando Gmail cada 60 segundos. Click para apagar."
                  : "Auto-consulta apagada. Click para prenderla (consulta cada 60s)."
              }
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={autoRefreshEnabled}
                onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
              />
              <span
                className={`relative inline-block w-8 h-4 rounded-full transition ${
                  autoRefreshEnabled ? "bg-green-500" : "bg-slate-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition ${
                    autoRefreshEnabled ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </span>
              {autoRefreshEnabled ? "Auto-consulta cada 60s" : "Auto-consulta apagada"}
            </label>
          )}
          {isMaster && (
            <Button variant="outline" onClick={() => { setConfigEmail(email || ""); setConfigPassword(""); setConfigWhatsapp(whatsappNumber); setShowConfigDialog(true); }} data-testid="configure-sinpe-btn">
              <Settings className="w-4 h-4 mr-2" /> Configurar
            </Button>
          )}
        </div>
      </div>

      {/* Top-level Tabs: SINPE Móvil vs Transferencias Bancarias */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setTopView("sinpe")}
          data-testid="tab-sinpe"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            topView === "sinpe"
              ? "border-blue-600 text-blue-700"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <span className="inline-flex items-center gap-2">
            <Mail className="w-4 h-4" /> SINPE Móvil
          </span>
        </button>
        <button
          onClick={() => setTopView("transfers")}
          data-testid="tab-transfers"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            topView === "transfers"
              ? "border-blue-600 text-blue-700"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <span className="inline-flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4" /> Transferencias entre Bancos
          </span>
        </button>
      </div>

      {topView === "transfers" ? (
        <BankTransfersTab selectedBox={selectedBox} configured={configured} />
      ) : (
        <>
        {/* SINPE MAIN CONTENT */}
      {/* Connection Status */}
      <Card className={configured ? "border-green-200 bg-green-50" : "border-slate-200"}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {configured ? <Wifi className="w-6 h-6 text-green-600" /> : <WifiOff className="w-6 h-6 text-slate-400" />}
              <div>
                {configured ? (
                  <><p className="font-medium text-green-700">Correo Conectado</p>
                  <div className="flex items-center gap-3">
                    <p className="text-sm text-green-600">{email}</p>
                    {whatsappNumber && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1"><MessageCircle className="w-3 h-3" />{whatsappNumber}</span>}
                  </div></>
                ) : (
                  <><p className="font-medium text-slate-700">Sin Correo Configurado</p><p className="text-sm text-slate-500">Configure un correo para consultar SINPE</p></>
                )}
              </div>
            </div>
            {configured && isMaster && <Button variant="ghost" size="sm" onClick={handleRemove} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>}
          </div>
        </CardContent>
      </Card>

      {/* Fetch Controls */}
      {configured && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Filter mode buttons */}
              <div className="flex gap-1">
                <Button size="sm" variant={filterMode === "today" ? "default" : "outline"} onClick={() => setFilterMode("today")}>Hoy</Button>
                <Button size="sm" variant={filterMode === "days" ? "default" : "outline"} onClick={() => setFilterMode("days")}>Días</Button>
                <Button size="sm" variant={filterMode === "range" ? "default" : "outline"} onClick={() => setFilterMode("range")}>
                  <Calendar className="w-3.5 h-3.5 mr-1" /> Rango
                </Button>
              </div>

              {filterMode === "days" && (
                <Select value={days} onValueChange={setDays}>
                  <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 día</SelectItem>
                    <SelectItem value="3">3 días</SelectItem>
                    <SelectItem value="7">7 días</SelectItem>
                    <SelectItem value="15">15 días</SelectItem>
                    <SelectItem value="30">30 días</SelectItem>
                  </SelectContent>
                </Select>
              )}

              {filterMode === "range" && (
                <div className="flex items-center gap-2">
                  <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36" />
                  <span className="text-sm text-slate-400">a</span>
                  <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36" />
                </div>
              )}

              <Button onClick={() => handleFetchSinpe(false)} disabled={fetching} data-testid="fetch-sinpe-btn">
                {fetching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                {fetching ? "Consultando..." : "Consultar"}
              </Button>
              {sinpeList.length > 0 && (
                <Button variant="outline" onClick={handleExportExcel} disabled={exporting} data-testid="export-sinpe-btn">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  Excel
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {sinpeList.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-slate-50 border-slate-200"><CardContent className="py-4 text-center"><p className="text-3xl font-bold text-slate-700">{sinpeList.length}</p><p className="text-xs text-slate-500 font-medium">Total</p></CardContent></Card>
          <Card className="bg-green-50 border-green-200"><CardContent className="py-4 text-center"><p className="text-3xl font-bold text-green-600">₡{totalEntrante.toLocaleString()}</p><p className="text-xs text-green-700 font-medium">{entrantes.length} Entrantes</p></CardContent></Card>
          <Card className="bg-red-50 border-red-200"><CardContent className="py-4 text-center"><p className="text-3xl font-bold text-red-600">₡{totalSaliente.toLocaleString()}</p><p className="text-xs text-red-700 font-medium">{salientes.length} Salientes</p></CardContent></Card>
          <Card className="bg-blue-50 border-blue-200"><CardContent className="py-4 text-center"><p className="text-3xl font-bold text-blue-600">₡{(totalEntrante - totalSaliente).toLocaleString()}</p><p className="text-xs text-blue-700 font-medium">Balance</p></CardContent></Card>
        </div>
      )}

      {/* Tabs + Search */}
      {sinpeList.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex gap-1">
            <Button size="sm" variant={activeTab === "entrante" ? "default" : "outline"} onClick={() => setActiveTab("entrante")} className={activeTab === "entrante" ? "bg-green-600 hover:bg-green-700" : "text-green-700 border-green-300"}>
              <ArrowDownLeft className="w-4 h-4 mr-1" /> Entrantes ({entrantes.length})
            </Button>
            <Button size="sm" variant={activeTab === "saliente" ? "default" : "outline"} onClick={() => setActiveTab("saliente")} className={activeTab === "saliente" ? "bg-red-600 hover:bg-red-700" : "text-red-700 border-red-300"}>
              <ArrowUpRight className="w-4 h-4 mr-1" /> Salientes ({salientes.length})
            </Button>
            <Button size="sm" variant={activeTab === "todos" ? "default" : "outline"} onClick={() => setActiveTab("todos")}>Todos ({sinpeList.length})</Button>
          </div>
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
          </div>
        </div>
      )}

      {/* SINPE List */}
      {sinpeList.length > 0 && filtered.length > 0 ? (
        <div className="space-y-2">
          <div className="hidden lg:grid gap-2 px-4 py-2 bg-slate-100 rounded-lg text-xs font-semibold text-slate-600" style={{ gridTemplateColumns: "50px 130px 2fr 80px 100px 80px 1.5fr 80px" }}>
            <span>Tipo</span><span>Fecha/Hora</span><span>Nombre</span><span>Tel</span><span className="text-right">Monto</span><span>Ref.</span><span>Motivo</span><span className="text-center">Acción</span>
          </div>
          {filtered.map((sinpe, idx) => {
            const sent = isSent(sinpe);
            return (
              <div key={sinpe.email_id || idx} className={`grid gap-2 px-4 py-3 border rounded-lg hover:shadow-sm transition-shadow items-center ${sinpe.direction === "saliente" ? "bg-red-50 border-red-100" : "bg-white"}`} style={{ gridTemplateColumns: "50px 130px 2fr 80px 100px 80px 1.5fr 80px" }} data-testid="sinpe-row">
                <span>
                  {sinpe.direction === "saliente" ? (
                    <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-red-600 bg-red-100 px-1.5 py-0.5 rounded"><ArrowUpRight className="w-3 h-3" />SAL</span>
                  ) : (
                    <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-green-600 bg-green-100 px-1.5 py-0.5 rounded"><ArrowDownLeft className="w-3 h-3" />ENT</span>
                  )}
                </span>
                <span className="text-xs text-slate-600">
                  {sinpe.date ? (
                    <>
                      <span className="block font-medium">{new Date(sinpe.date).toLocaleDateString("es-CR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                      <span className="text-slate-400">{new Date(sinpe.date).toLocaleTimeString("es-CR", { hour: "2-digit", minute: "2-digit" })}</span>
                    </>
                  ) : "-"}
                </span>
                <div>
                  <p className="text-sm font-medium text-slate-800">{sinpe.sender_name || "Desconocido"}</p>
                  <p className="text-xs text-slate-400">
                    {sinpe.bank}
                    {sinpe.sender_bank && sinpe.sender_bank !== sinpe.bank ? ` · Origen: ${sinpe.sender_bank}` : ""}
                  </p>
                </div>
                <span className="text-sm text-slate-600 font-mono">{sinpe.sender_phone || "-"}</span>
                <span className={`text-right text-sm font-bold ${sinpe.direction === "saliente" ? "text-red-600" : "text-green-600"}`}>
                  {sinpe.direction === "saliente" ? "-" : "+"}₡{sinpe.amount?.toLocaleString() || "0"}
                </span>
                <span className="text-xs text-slate-500 font-mono truncate" title={sinpe.reference}>{sinpe.reference ? sinpe.reference.slice(-8) : "-"}</span>
                <span className="text-xs text-slate-500 truncate" title={sinpe.description}>{sinpe.description || "-"}</span>
                <div className="flex items-center justify-center gap-1">
                  <button onClick={() => handleSendWhatsapp(sinpe)} className={`p-1.5 rounded-lg transition-colors ${sent ? "bg-slate-200 text-slate-400" : "bg-green-500 text-white hover:bg-green-600"}`} title={sent ? "Ya enviado" : "Enviar por WhatsApp"} data-testid="send-whatsapp-btn">
                    {sent ? <Check className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />}
                  </button>
                  <button onClick={() => openRawBody(sinpe)} className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors" title="Ver cuerpo del correo (útil para depurar nombres)" data-testid="view-raw-body-btn">
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
          <div className={`grid gap-2 px-4 py-3 border-2 rounded-lg font-bold ${activeTab === "saliente" ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`} style={{ gridTemplateColumns: "50px 130px 2fr 80px 100px 80px 1.5fr 80px" }}>
            <span></span><span></span><span className="text-slate-700">TOTAL ({filtered.length})</span><span></span>
            <span className={`text-right text-lg ${activeTab === "saliente" ? "text-red-700" : "text-green-700"}`}>₡{filteredTotal.toLocaleString()}</span>
            <span></span><span></span><span></span>
          </div>
        </div>
      ) : sinpeList.length > 0 && filtered.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-slate-500">No hay SINPE {activeTab === "entrante" ? "entrantes" : activeTab === "saliente" ? "salientes" : ""}</CardContent></Card>
      ) : configured && !fetching ? (
        <Card><CardContent className="py-12 text-center text-slate-500"><Mail className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Presione "Consultar" para buscar SINPE</p></CardContent></Card>
      ) : !configured ? (
        <Card><CardContent className="py-12 text-center text-slate-500"><WifiOff className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Configure un correo SINPE para esta caja</p>
          {isMaster && <Button className="mt-4" onClick={() => setShowConfigDialog(true)}><Settings className="w-4 h-4 mr-2" /> Configurar</Button>}
        </CardContent></Card>
      ) : null}

      {/* Configure Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Configurar SINPE</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">El sistema solo <strong>lee</strong> los correos, no los borra ni modifica.</p>
            <div><Label>Correo Gmail</Label><Input type="email" placeholder="correo@gmail.com" value={configEmail} onChange={(e) => setConfigEmail(e.target.value)} data-testid="sinpe-config-email" /></div>
            <div><Label>Contraseña de Aplicación</Label><Input type="password" placeholder="xxxx xxxx xxxx xxxx" value={configPassword} onChange={(e) => setConfigPassword(e.target.value)} data-testid="sinpe-config-password" /><p className="text-xs text-slate-400 mt-1">Genere en: myaccount.google.com/apppasswords</p></div>
            <div className="border-t pt-4">
              <Label className="flex items-center gap-2"><MessageCircle className="w-4 h-4 text-green-600" /> Número WhatsApp</Label>
              <Input type="tel" placeholder="88887777" value={configWhatsapp} onChange={(e) => setConfigWhatsapp(e.target.value)} data-testid="sinpe-config-whatsapp" />
              <p className="text-xs text-slate-400 mt-1">
                Número al que se enviarán los SINPE. Deje <strong>vacío</strong> para quitar el número.
              </p>
              <p className="text-xs text-blue-600 mt-1">
                💡 Si solo querés cambiar/quitar el número, deja vacíos los campos de correo y contraseña.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancelar</Button>
            <Button
              onClick={handleConfigure}
              disabled={configuring || (!configEmail && !configPassword && configWhatsapp === (whatsappNumber || ""))}
              data-testid="save-sinpe-config-btn"
            >
              {configuring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              {configuring ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raw Body Debug Viewer */}
      <Dialog open={rawBodyOpen} onOpenChange={setRawBodyOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="raw-body-dialog">
          <DialogHeader>
            <DialogTitle>Cuerpo del correo SINPE</DialogTitle>
          </DialogHeader>
          {rawBodyContent && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-3 rounded border">
                <div><b>Banco detectado:</b> {rawBodyContent.bank || "—"}</div>
                <div><b>Dirección:</b> {rawBodyContent.direction || "—"}</div>
                <div className="col-span-2"><b>De:</b> <span className="font-mono">{rawBodyContent.from || "—"}</span></div>
                <div className="col-span-2"><b>Asunto:</b> {rawBodyContent.subject || "—"}</div>
                <div><b>Nombre extraído:</b> <span className={rawBodyContent.sender_name ? "text-green-700 font-semibold" : "text-red-700 font-semibold"}>{rawBodyContent.sender_name || "(VACÍO)"}</span></div>
                <div><b>Monto:</b> ₡{rawBodyContent.amount?.toLocaleString() || "0"}</div>
              </div>
              <div>
                <Label className="flex items-center justify-between">
                  <span>Cuerpo del correo (primeros 500 caracteres)</span>
                  <Button size="sm" variant="outline" onClick={copyRawBody}>Copiar</Button>
                </Label>
                <pre className="bg-slate-900 text-slate-100 text-xs p-3 rounded mt-1 whitespace-pre-wrap break-words max-h-96 overflow-y-auto font-mono">
{rawBodyContent.body}
                </pre>
              </div>
              {!rawBodyContent.sender_name && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded">
                  No se extrajo el nombre. Copie este cuerpo y compártalo para ajustar el parser del banco <b>{rawBodyContent.bank}</b>.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}
